import { useCallback, useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button, FormContainerControlled } from "components";
import { HookForm, TFormFieldConfig } from "pages/Components";
import { useForm } from "react-hook-form";
import { i18n } from "translations";
import { getRequiredMessage } from "utils";
import { getPrimaryColor } from "utils/theme";
import * as Yup from "yup";

import { useSignIn } from "./hooks";

interface PasswordLoginProps {
  onLogin: ReturnType<typeof useSignIn>["handleLoginPassword"];
  loading?: boolean;
}

const FormSchema = Yup.object({
  email: Yup.string()
    .email(i18n.t("emailIsInvalid"))
    .required(getRequiredMessage(i18n.t("email"))),
  password: Yup.string()
    .min(6)
    .required(getRequiredMessage(i18n.t("password"))),
});
type TForm = Yup.InferType<typeof FormSchema>;

const PasswordLogin: React.FC<PasswordLoginProps> = ({ onLogin, loading }) => {
  const methods = useForm<TForm>({
    resolver: yupResolver(FormSchema) as never,
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const configs = useMemo((): TFormFieldConfig[] => {
    return [
      {
        col: 12,
        name: "email",
        type: "text",
        label: i18n.t("email"),
        required: true,
      },
      {
        col: 12,
        name: "password",
        type: "text",
        label: i18n.t("password"),
        required: true,
        extendProps: { type: "password" },
      },
    ];
  }, []);

  const handleSubmit = useCallback(
    (form: TForm) => {
      onLogin(form.email, form.password);
    },
    [onLogin]
  );

  return (
    <FormContainerControlled methods={methods} onSubmit={handleSubmit}>
      <Stack gap={6}>
        <HookForm fields={configs} />
        <Button
          variant="outlined"
          size="lg"
          type="submit"
          useTypography
          loading={loading}
          sx={{ background: getPrimaryColor()[50] }}
        >
          {i18n.t("signIn")}
        </Button>
      </Stack>
    </FormContainerControlled>
  );
};

export default PasswordLogin;
